import type {
  CreateRestaurantInput,
  DayOfWeek,
  DaySchedule,
  LocalizedRestaurant,
  LocalizedText,
  UpdateRestaurantInput,
  WeeklySchedule,
} from '@noor/eats-api';
import type { ApiLocale } from '@noor/api';
import { MENU_SOURCES, type MenuSource } from '@/entities/branch';
import type { BranchFormValues } from '../model/schema';

// Mapping between the branch drawer's form values and the merchant API's
// create/update bodies.

/** Weekday order for the schedule rows — Monday-first, the design's order. */
const WEEKDAY_ORDER: readonly DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

/** API weekday → the `venue.weekdays.<key>` i18n key each row renders. */
const LABEL_BY_DAY: Record<DayOfWeek, string> = {
  MONDAY: 'mon',
  TUESDAY: 'tue',
  WEDNESDAY: 'wed',
  THURSDAY: 'thu',
  FRIDAY: 'fri',
  SATURDAY: 'sat',
  SUNDAY: 'sun',
  DAY_OF_WEEK_UNSPECIFIED: '',
};

/** Opening window a brand-new branch starts with (the design's placeholder). */
const DEFAULT_OPEN = '08:00';
const DEFAULT_CLOSE = '00:00';

/** API names for the required fields checked when a new branch is submitted. */
export function missingCreateBranchFields(errors: {
  address?: { type?: string };
  backgroundImage?: { type?: string };
  icon?: { type?: string };
  minPrepTime?: { type?: string };
  maxPrepTime?: { type?: string };
}): string[] {
  const required = [
    { formField: 'address', apiField: 'address_line_uz' },
    { formField: 'backgroundImage', apiField: 'background_image' },
    { formField: 'icon', apiField: 'icon' },
    { formField: 'minPrepTime', apiField: 'min_prep_time' },
    { formField: 'maxPrepTime', apiField: 'max_prep_time' },
  ] as const;

  return required
    .filter(({ formField }) => {
      const error = errors[formField];
      // The address also receives a custom error when the map location has not
      // been picked; that is a different validation problem from a missing API
      // address line and is already shown beside the address field.
      return error !== undefined && !(formField === 'address' && error.type === 'custom');
    })
    .map(({ apiField }) => apiField);
}

/**
 * Seven form rows from the stored schedule, Monday-first. Days the API didn't
 * return are shown open on the default window rather than dropped, so the form
 * always renders exactly seven checkboxes.
 */
export function toDayRows(schedule: WeeklySchedule): BranchFormValues['days'] {
  const byDay = new Map(schedule.map((day) => [day.day_of_week, day]));

  return WEEKDAY_ORDER.map((weekday) => {
    const day = byDay.get(weekday);
    const isOpen = day ? !day.is_closed : true;
    // A closed day carries EMPTY times, not absent ones, so the fallback must
    // treat `''` as "no time" — `??` would keep the empty string and the time
    // picker would render blank.
    return {
      label: LABEL_BY_DAY[weekday],
      isOpen,
      orderFrom: day?.order_from ?? '',
      orderTo: day?.order_to ?? '',
      open:
        day?.open_time === undefined || day.open_time === ''
          ? DEFAULT_OPEN
          : day.open_time,
      close:
        day?.close_time === undefined || day.close_time === ''
          ? DEFAULT_CLOSE
          : day.close_time,
    };
  });
}

/**
 * Rebuild the API's 7-entry schedule from the form rows. A closed day is sent
 * with empty times and `is_closed`, which is how the backend stores "shut".
 */
export function toWeekSchedule(days: BranchFormValues['days']): DaySchedule[] {
  return days.map((row, index) => ({
    // Positional: the rows are a fixed Monday-first array, so the index is the
    // weekday. Reading `row.label` back would break if a label were ever
    // renamed for display.
    day_of_week: WEEKDAY_ORDER[index] ?? 'MONDAY',
    open_time: row.isOpen ? row.open : '',
    close_time: row.isOpen ? row.close : '',
    is_closed: !row.isOpen,
    order_from: row.isOpen ? (row.orderFrom ?? '') : '',
    order_to: row.isOpen ? (row.orderTo ?? '') : '',
  }));
}

/**
 * A `LocalizedText` with blank members dropped.
 *
 * An EMPTY STRING means "not translated yet", never "translated to empty" — the
 * translations table refuses to store an empty value — so a blank input must be
 * omitted rather than sent, or it would blank an existing translation.
 */
function toLocalizedText(values: Partial<Record<keyof LocalizedText, string>>) {
  const text: Partial<LocalizedText> = {};
  if (values.ru?.trim()) text.ru = values.ru.trim();
  if (values.uz?.trim()) text.uz = values.uz.trim();
  if (values.en?.trim()) text.en = values.en.trim();
  return text as LocalizedText;
}

/**
 * The fields shared by the create and update bodies. Photos are deliberately
 * excluded: the create body carries them inline while the update sends them
 * through the per-slot image endpoints, so each caller adds its own.
 */
function toCommonInput(values: BranchFormValues) {
  return {
    name: toLocalizedText({ ru: values.name }),
    phone: values.phone,
    address: {
      // The address is entered once in Uzbek and sent for both required
      // address translations until the form offers locale-specific inputs.
      line: toLocalizedText({ ru: values.address, uz: values.address }),
      latitude: values.latitude,
      longitude: values.longitude,
    },
    description: toLocalizedText({
      ru: values.descriptionRu,
      uz: values.descriptionUz,
      en: values.descriptionEn,
    }),
    ...(values.menuSource ? { menu_source: values.menuSource } : {}),
    // `delivery_provider` is deliberately NOT sent. It is a provisioning
    // choice made outside the panel, so echoing it back on every save would
    // let a stale form value overwrite it.
    ...(values.freeDeliveryThreshold !== undefined
      ? { free_delivery_threshold: values.freeDeliveryThreshold }
      : {}),
    restaurant_category_ids: values.restaurantCategoryIds,
    pickup_available: values.pickupAvailable,
    week_schedule: toWeekSchedule(values.days),
    min_prep_time: values.minPrepTime,
    max_prep_time: values.maxPrepTime,
  };
}

/**
 * The `POST /restaurants` body. Photo keys are passed inline — a branch that
 * doesn't exist yet has no id to address an image slot with.
 */
export function toCreateInput(
  values: BranchFormValues,
  iconKey: string | undefined,
  backgroundKey: string | undefined,
): CreateRestaurantInput {
  return {
    ...toCommonInput(values),
    cuisine_ids: values.cuisineIds,
    // Create: a number or nothing — `null` is only meaningful on PATCH.
    ...(values.minOrderAmount !== undefined
      ? { min_order_amount: values.minOrderAmount }
      : {}),
    ...(iconKey ? { icon: iconKey } : {}),
    ...(backgroundKey ? { background_image: backgroundKey } : {}),
  };
}

/**
 * The `PATCH /restaurants/{id}` body. NO photo fields: they were removed from
 * this body when photos became slot-managed, and the gateway rejects unknown
 * fields outright rather than ignoring them.
 */
export function toUpdateInput(values: BranchFormValues): UpdateRestaurantInput {
  // Always sent: a number sets the minimum and `null` clears it. The form is
  // seeded with the stored value, so an untouched field round-trips unchanged.
  return {
    ...toCommonInput(values),
    cuisine_ids: { ids: values.cuisineIds },
    min_order_amount: values.minOrderAmount ?? null,
  };
}

/** Form defaults for a brand-new branch. */
export function emptyDefaults(): BranchFormValues {
  return {
    name: '',
    phone: '',
    address: '',
    latitude: 0,
    longitude: 0,
    restaurantCategoryIds: [],
    cuisineIds: [],
    pickupAvailable: true,
    menuSource: '',
    descriptionRu: '',
    descriptionUz: '',
    descriptionEn: '',
    days: toDayRows([]),
    minPrepTime: 0,
    maxPrepTime: 0,
  };
}

/**
 * Form defaults seeded from a branch being edited. The localized read carries
 * every translation, so the three description inputs each get their own.
 *
 * Stored photos are marked `saved`: their URL is a one-way expansion of a
 * storage key, so they can be shown and removed but never re-sent.
 */
export function toDefaults(
  restaurant: LocalizedRestaurant,
  schedule: WeeklySchedule,
  locale: ApiLocale = 'ru',
): BranchFormValues {
  const localized = (values: LocalizedText, preferred: ApiLocale = locale): string => {
    const order = [preferred, 'ru', 'uz', 'en'] as const;
    return order.map((key) => values[key]).find((value) => value.trim() !== '') ?? '';
  };

  return {
    name: localized(restaurant.name),
    phone: restaurant.phone ?? '',
    address: restaurant.address ? localized(restaurant.address.line) : '',
    latitude: restaurant.address?.latitude ?? 0,
    longitude: restaurant.address?.longitude ?? 0,
    ...(restaurant.icon ? { icon: { url: restaurant.icon, saved: true } } : {}),
    ...(restaurant.background_image
      ? { backgroundImage: { url: restaurant.background_image, saved: true } }
      : {}),
    restaurantCategoryIds: [...(restaurant.restaurant_category_ids ?? [])],
    cuisineIds: (restaurant.cuisines ?? []).map((cuisine) => cuisine.id),
    pickupAvailable: restaurant.pickup_available ?? true,
    // Only seed a source the select can actually show. A stored value outside
    // the catalogue (or an empty one) seeds BLANK rather than being handed to a
    // `Select` that would render nothing and then write that nothing back on
    // the next save — `toCommonInput` omits an empty `menu_source`, so leaving
    // it blank preserves the stored value instead of clearing it.
    menuSource: isKnownMenuSource(restaurant.menu_source) ? restaurant.menu_source : '',
    ...(restaurant.free_delivery_threshold
      ? { freeDeliveryThreshold: restaurant.free_delivery_threshold }
      : {}),
    ...(restaurant.min_order_amount
      ? { minOrderAmount: restaurant.min_order_amount }
      : {}),
    descriptionRu: localized(restaurant.description, 'ru'),
    descriptionUz: localized(restaurant.description, 'uz'),
    descriptionEn: localized(restaurant.description, 'en'),
    days: toDayRows(schedule),
    minPrepTime: restaurant.min_prep_time,
    maxPrepTime: restaurant.max_prep_time,
  };
}

/** Whether a stored `menu_source` is one the form's select can display. */
function isKnownMenuSource(source: string): source is MenuSource {
  return (MENU_SOURCES as readonly string[]).includes(source);
}
